import { apiClient } from '@/core/api/client'

// "Appels" page API — office-wide call history + shared voicemail
// INBOX, proxied by the chatapp backend (see backend routes/voip.js,
// VoipHistoryController). Audio is fetched as authed blobs so no token
// ever appears in an <audio src>.

export interface VoipCall {
  id: string
  callId: string
  direction: 'inbound' | 'outbound'
  externalNumber: string | null
  contactName: string | null
  agentName: string | null
  agentEmail: string | null
  startedAt: string | null
  durationMs: number
  endCause: string | null
  missed: boolean
  /** Answered by the voicemail app, not a human. */
  voicemail: boolean
  affaireId: number | null
  hasRecording: boolean
  summary: string | null
}

export interface Voicemail {
  id: string
  caller: string
  contactName: string | null
  when: string | null
  durationSec: number | null
  affaireId: number | null
  attributedBy: string | null
  readAt: string | null
  readBy: string | null
  archivedAt: string | null
  archivedBy: string | null
  /** Whisper + résumé GPT — même pipeline que les enregistrements d'appels. */
  transcription: string | null
  summary: string | null
  transcriptionStatus: 'done' | 'pending' | 'error' | null
  /** Derniers dossiers ACTIFS du client identifié par le numéro. */
  suggestedAffaires: SuggestedAffaire[]
  /** Tâche mes-tâches créée depuis ce message (« envoyer en tâche »). */
  taskId: string | null
  taskAssignedTo: string | null
  taskCreatedAt: string | null
  taskDone: boolean | null
}

export interface SuggestedAffaire {
  id: number | string
  status: string | null
  address: string | null
}

export const appelsAPI = {
  async listCalls(limit = 200): Promise<VoipCall[]> {
    const res = await apiClient.get<{ calls: VoipCall[] }>(
      `/api/voip/cdr?limit=${limit}`,
    )
    return res.calls ?? []
  },

  async listVoicemails(): Promise<Voicemail[]> {
    const res = await apiClient.get<{ voicemails: Voicemail[] }>(
      '/api/voip/voicemails',
    )
    return res.voicemails ?? []
  },

  callRecordingBlob(cdrId: string): Promise<Blob> {
    return apiClient.get<Blob>(`/api/voip/cdr/${cdrId}/recording`, {
      responseType: 'blob',
    })
  },

  voicemailAudioBlob(voicemailId: string): Promise<Blob> {
    return apiClient.get<Blob>(`/api/voip/voicemails/${voicemailId}/audio`, {
      responseType: 'blob',
    })
  },

  async setAttribution(params: {
    caller: string
    when: string
    affaireId: number | null
    voicemailId?: string
  }): Promise<void> {
    await apiClient.put('/api/voip/voicemails/attribution', params)
  },

  /**
   * Lie / re-lie / délie (affaireId null) un appel du journal à une
   * affaire. Les métadonnées CDR accompagnent la requête pour que le
   * lien soit complet dans la chronologie du tracker même si le
   * cdr-writer n'a rien publié pour cet appel.
   */
  async setCallAffaire(
    callId: string,
    affaireId: number | null,
    meta?: {
      callerNumber?: string
      direction?: 'inbound' | 'outbound'
      startedAt?: string
      durationMs?: number
    },
  ): Promise<void> {
    await apiClient.put(`/api/voip/call-recordings/${callId}/affaire`, {
      affaireId,
      ...meta,
    })
  },

  /** Envoie le message vocal comme tâche mes-tâches à un utilisateur. */
  async sendAsTask(params: {
    caller: string
    when: string
    assignedToUserId: string
    comment?: string
  }): Promise<{ taskId: string; taskAssignedTo: string; taskCreatedAt: string }> {
    return apiClient.post('/api/voip/voicemails/task', params)
  },

  async setRead(params: {
    caller: string
    when: string
    read: boolean
  }): Promise<void> {
    await apiClient.put('/api/voip/voicemails/read', params)
  },

  async setArchived(params: {
    caller: string
    when: string
    archived: boolean
  }): Promise<void> {
    await apiClient.put('/api/voip/voicemails/archive', params)
  },
}
